/*
 * pic-nn-web-ui
 *
 * Web UI for neural network applications.
 *
 * @copyright 2020 VSTE CB <http://www.vstecb.cz/>.
 * @license See LICENSE file distributed with this source code for more information.
 */

import { ModelInput, IModelInputBaseConfig } from "../ModelInput";
import { TValidatonError, VALIDATION_ERROR_TYPE } from "../IValidationError";

export interface IInputNumberConfig extends IModelInputBaseConfig {
	/** Minimal allowed value */
	min?: number;
	/** Maximal allowed value */
	max?: number;
}

/**
 * Input for numeric value
 */
export class ModelInputNumber extends ModelInput<string, number, IInputNumberConfig> {

	protected parse(newValue) {

		if (newValue === "") {
			return 0;
		}

		const val = parseFloat(newValue);

		if (!isNaN(val)) {
			return val;
		} else {
			return 0;
		}

	}

	protected validate(inputValue: string, value: number): Array<TValidatonError> {

		// Check required
		if (this.config.required === true && inputValue.trim() === "") {
			return [{
				type: VALIDATION_ERROR_TYPE.REQUIRED,
				message: "Value is required."
			} as TValidatonError];
		}

		// Skip if not required and empty
		if (inputValue.trim() === "") {
			return [];
		}

		// Check if value is number
		if (isNaN(parseFloat(inputValue))) {
			return [{
				type: VALIDATION_ERROR_TYPE.INVALID_NUMBER,
				message: "Value is not a valid number."
			} as TValidatonError];
		}

		// Check min bound
		if (this.config.min !== undefined && value < this.config.min) {
			return [{
				type: VALIDATION_ERROR_TYPE.VALUE_MIN,
				message: `Value must be greater or equal to '${this.config.min}'.`,
				min: this.config.min
			}];
		}

		// Check max bound
		if (this.config.max !== undefined && value > this.config.max) {
			return [{
				type: VALIDATION_ERROR_TYPE.VALUE_MAX,
				message: `Value must be lower or equal to '${this.config.max}'.`,
				max: this.config.max
			}];
		}

		return [];

	}

}

/**
 * Numeric field
 *
 * @param opts Options
 */
export function inputNumber(opts: IInputNumberConfig) {

	return (handler) =>
		new ModelInputNumber(handler, "number", "0", opts);

}
